import { KH_VERTICAL_ID } from "constants/config";
import {
  PROGRAM_STATUS,
  WEEKDAY_LABELS,
} from "constants/batchPreference";

export const getOrderTenantId = (order) => {
  if (!order) return null;
  const tenantId =
    order.tenant_id ??
    order.tenantId ??
    order.tenant?.id ??
    order.packages?.[0]?.tenant_id ??
    order.packages?.[0]?.tenantId;
  return tenantId == null || tenantId === "" ? null : tenantId;
};

const normalizeVerticalList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.verticals)) return payload.verticals;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const getTenantGatewayId = (item) => {
  if (!item || typeof item !== "object") return null;
  const value = item.gateway_tenant_id ?? item.gatewayTenantId ?? null;
  if (value == null || value === "") return null;
  return value;
};

const getTenantGatewayBranchId = (item) => {
  if (!item || typeof item !== "object") return null;
  const value = item.gateway_branch_id ?? item.gatewayBranchId ?? null;
  if (value == null || value === "") return null;
  return value;
};

const findMatchingCatalogTenants = (verticalAndTenants, order) => {
  const verticals = normalizeVerticalList(verticalAndTenants);
  if (!verticals.length) return [];

  const orderTenantId = getOrderTenantId(order);
  if (orderTenantId == null) return [];

  const verticalId = order?.business_unit_id ?? order?.businessUnitId;
  const matchingVerticals = verticalId
    ? verticals.filter((vertical) => Number(vertical.id) === Number(verticalId))
    : [];
  const searchList = matchingVerticals.length ? matchingVerticals : verticals;
  const tenants = searchList.flatMap((vertical) => vertical.tenants || []);

  return tenants.filter(
    (tenant) =>
      String(tenant.id) === String(orderTenantId) ||
      String(tenant.tenant_id) === String(orderTenantId),
  );
};

export const getGatewayTenantId = (verticalAndTenants, order) => {
  const exactMatches = findMatchingCatalogTenants(verticalAndTenants, order);
  const gatewayIds = [
    ...new Set(exactMatches.map(getTenantGatewayId).filter(Boolean)),
  ];

  if (gatewayIds.length !== 1) return null;
  return gatewayIds[0];
};

export const getGatewayBranchId = (verticalAndTenants, order) => {
  const exactMatches = findMatchingCatalogTenants(verticalAndTenants, order);
  const branchIds = [
    ...new Set(exactMatches.map(getTenantGatewayBranchId).filter(Boolean)),
  ];

  if (branchIds.length !== 1) return null;
  return branchIds[0];
};

export const normalizeUamId = (value) => {
  if (value == null) return null;
  const asString = String(value).trim();
  if (!asString || asString === "null" || asString === "undefined") return null;
  return asString;
};

const isTruthyFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

export const isSmartBatchingEnabled = (pkg) =>
  isTruthyFlag(
    pkg?.is_smart_batching_enabled ??
      pkg?.isSmartBatchingEnabled ??
      pkg?.smart_batching_enabled,
  );

export const isOrderEligibleForBatchPreference = (order) => {
  if (!order) return false;
  if (Number(order.business_unit_id) === KH_VERTICAL_ID) return false;
  return (order.packages || []).some((pkg) => isSmartBatchingEnabled(pkg));
};

export const formatTimeInIst = (timing) => {
  if (!timing) return "";
  const [hoursRaw, minutesRaw] = String(timing).split(":");
  const hours = Number(hoursRaw);
  const minutes = minutesRaw || "00";
  if (Number.isNaN(hours)) return timing;

  const period = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).slice(0, 2).padStart(2, "0")} ${period} IST`;
};

export const getScheduleDayTimeLabels = (frequency = [], scheduleOrTiming) => {
  const timeLabel =
    typeof scheduleOrTiming === "object" && scheduleOrTiming !== null
      ? formatScheduleTimeInIst(scheduleOrTiming)
      : formatTimeInIst(scheduleOrTiming);
  return (frequency || []).map((day) => {
    const dayLabel = WEEKDAY_LABELS[day] || `Day ${day}`;
    return timeLabel ? `${dayLabel} ${timeLabel}` : dayLabel;
  });
};

export const getScheduleEntries = (batchOrDecision = {}) => {
  const source = batchOrDecision || {};
  if (Array.isArray(source.schedules) && source.schedules.length) {
    return source.schedules;
  }
  if (source.schedule) return [source.schedule];
  return [];
};

export const getBatchScheduleDayTimeLabels = (batchOrDecision = {}) =>
  getScheduleEntries(batchOrDecision).flatMap((entry) =>
    getScheduleDayTimeLabels(entry?.frequency, entry),
  );

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const parseDateParts = (isoDate) => {
  const match = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return {
      year: Number(match[1]),
      monthIndex: Number(match[2]) - 1,
      day: Number(match[3]),
    };
  }
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;
  return {
    year: date.getFullYear(),
    monthIndex: date.getMonth(),
    day: date.getDate(),
  };
};

export const formatStartDate = (isoDate) => {
  const parts = parseDateParts(isoDate);
  if (!parts) return isoDate || "";
  return `${String(parts.day).padStart(2, "0")} ${MONTH_LABELS[parts.monthIndex]} ${parts.year}`;
};

const toSortableDate = (value) => {
  const parts = parseDateParts(value);
  if (!parts) return Number.MAX_SAFE_INTEGER;
  return parts.year * 10000 + (parts.monthIndex + 1) * 100 + parts.day;
};

export const getStartDateStatus = (isoDate, now = new Date()) => {
  if (!isoDate) return "";
  const todayValue =
    now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  return toSortableDate(isoDate) <= todayValue ? "Just Started" : "Upcoming";
};

export const getEarliestStartDate = (startDates = []) => {
  if (!startDates.length) return null;
  return [...startDates].sort(
    (left, right) => toSortableDate(left) - toSortableDate(right),
  )[0];
};

const IST_OFFSET_MINUTES = 5 * 60 + 30;

const parseClockParts = (clock) => {
  if (!clock) return null;
  const [hoursRaw, minutesRaw, secondsRaw] = String(clock).split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const seconds = Number(secondsRaw) || 0;
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return { hours, minutes, seconds };
};

const formatClock = (hours, minutes, seconds) =>
  `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
    seconds,
  ).padStart(2, "0")}`;

const shiftClock = (clock, minuteDelta) => {
  const parts = parseClockParts(clock);
  if (!parts) return "";
  const total = parts.hours * 60 + parts.minutes + minuteDelta;
  const normalized = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  return formatClock(
    Math.floor(normalized / 60),
    normalized % 60,
    parts.seconds,
  );
};

export const istTimeToUtc = (istTime) =>
  shiftClock(istTime, -IST_OFFSET_MINUTES);

export const utcTimeToIst = (utcTime) =>
  shiftClock(utcTime, IST_OFFSET_MINUTES);

export const resolveIstClockTime = (schedule = {}) => {
  if (!schedule || typeof schedule !== "object") return "";

  const istTime = schedule.timing_in_ist ?? schedule.timingInIst;
  if (istTime) return String(istTime);

  const utcTime = schedule.timing_in_utc ?? schedule.timingInUtc;
  if (utcTime) return utcTimeToIst(utcTime);

  if (schedule.timing) return String(schedule.timing);

  return "";
};

export const formatScheduleTimeInIst = (schedule = {}) =>
  formatTimeInIst(resolveIstClockTime(schedule));

export const getScheduleDisplayTime = (schedule = {}) =>
  resolveIstClockTime(schedule);

export const getScheduleUtcTime = (schedule = {}) =>
  schedule.timing_in_utc ||
  (schedule.timing_in_ist ? istTimeToUtc(schedule.timing_in_ist) : "");

export const areAllProgramsResolved = (programs = []) =>
  programs.length > 0 &&
  programs.every(
    (program) =>
      program.status === PROGRAM_STATUS.CONFIRMED ||
      program.status === PROGRAM_STATUS.SKIPPED,
  );

export const getNextPendingProgramId = (programs = [], currentId) => {
  const currentIndex = programs.findIndex((program) => program.id === currentId);
  const afterCurrent = programs.slice(currentIndex + 1);
  const beforeCurrent = programs.slice(0, currentIndex + 1);
  const next =
    afterCurrent.find((program) => program.status === PROGRAM_STATUS.PENDING) ||
    beforeCurrent.find((program) => program.status === PROGRAM_STATUS.PENDING);
  return next ? next.id : null;
};

export const buildStudentPayload = ({
  userId,
  order,
  gatewayTenantId,
  gatewayBranchId,
}) => ({
  userId: String(userId),
  verticalId: Number(order?.business_unit_id ?? order?.businessUnitId),
  tenantId: String(gatewayTenantId),
  branchId: String(gatewayBranchId ?? ""),
});

export const buildSuggestedProgramsPayload = ({
  orderId,
  userId,
  order,
  gatewayTenantId,
  gatewayBranchId,
}) => ({
  orderId,
  student: buildStudentPayload({
    userId,
    order,
    gatewayTenantId,
    gatewayBranchId,
  }),
});

export const buildPreferenceSuggestionsPayload = ({
  userId,
  order,
  gatewayTenantId,
  gatewayBranchId,
}) => ({
  student: buildStudentPayload({
    userId,
    order,
    gatewayTenantId,
    gatewayBranchId,
  }),
});

export const normalizeSuggestedPrograms = (payload) => {
  if (!payload) return [];
  const list = Array.isArray(payload?.programs)
    ? payload.programs
    : Array.isArray(payload?.suggestedPrograms)
      ? payload.suggestedPrograms
      : Array.isArray(payload?.data?.programs)
        ? payload.data.programs
        : Array.isArray(payload?.data?.suggestedPrograms)
          ? payload.data.suggestedPrograms
          : [];

  const programs = list
    .map((item) => {
      const id = item.programId ?? item.program_id;
      const name = item.programName ?? item.program_name;
      if (!id) return null;
      return {
        id: String(id),
        name: name || "Program",
      };
    })
    .filter(Boolean);

  const unique = [];
  const seen = new Set();
  programs.forEach((program) => {
    if (seen.has(program.id)) return;
    seen.add(program.id);
    unique.push(program);
  });
  return unique;
};

export const normalizeRecommendedSchedules = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.data?.preferences ||
      payload?.data?.peferences ||
      payload?.preferences ||
      payload?.peferences ||
      payload?.data?.schedules ||
      payload?.data ||
      payload?.recommendations ||
      [];

  const preferenceList = Array.isArray(list) ? list : [];

  return preferenceList.map((item, index) => {
    const scheduleEntries = resolveScheduleEntries(item);
    const primary = scheduleEntries[0] || {};
    const frequency = normalizeFrequency(primary.frequency || primary.days || []);
    const timingInIst =
      primary.timing_in_ist || primary.timingInIst || primary.timing || "";
    const timingInUtc =
      primary.timing_in_utc || primary.timingInUtc || "";

    const schedules = scheduleEntries.map((entry) => {
      const entryFrequency = normalizeFrequency(
        entry.frequency || entry.days || [],
      );
      const entryIst =
        entry.timing_in_ist || entry.timingInIst || entry.timing || "";
      const entryUtc = entry.timing_in_utc || entry.timingInUtc || "";
      return {
        frequency: entryFrequency,
        timing_in_ist: entryIst || (entryUtc ? utcTimeToIst(entryUtc) : ""),
        timing_in_utc: entryUtc || (entryIst ? istTimeToUtc(entryIst) : ""),
      };
    });

    return {
      id: String(item.id || item.batch_id || item.batchId || index),
      label: `Batch ${index + 1}`,
      schedule: {
        frequency,
        timing_in_ist:
          timingInIst || (timingInUtc ? utcTimeToIst(timingInUtc) : ""),
        timing_in_utc:
          timingInUtc || (timingInIst ? istTimeToUtc(timingInIst) : ""),
      },
      schedules,
      startDates: item.start_dates || item.startDates || [],
    };
  });
};

export const normalizePreferenceSuggestions = (payload) =>
  normalizeRecommendedSchedules(payload);

const resolveScheduleEntries = (item = {}) => {
  const candidates = [
    item.schedule,
    item.schedules,
    item.preference_schedule,
    item.preferenceSchedule,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length) {
      return candidate.filter((entry) => entry && typeof entry === "object");
    }
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      return [candidate];
    }
  }

  if (
    item.frequency ||
    item.days ||
    item.timing_in_utc ||
    item.timing_in_ist ||
    item.timing
  ) {
    return [item];
  }

  return [];
};

const normalizeFrequency = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((day) => Number(day))
    .filter((day) => Number.isFinite(day) && day >= 0 && day <= 7)
    .map((day) => (day === 0 ? 7 : day));
};

const toIsoBatchStartDate = (value) => {
  if (!value) return value;
  const asString = String(value).trim();
  if (!asString) return asString;
  if (asString.includes("T")) {
    const date = new Date(asString);
    return Number.isNaN(date.getTime()) ? asString : date.toISOString();
  }
  const date = new Date(`${asString}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return asString;
  return date.toISOString();
};

export const buildProcessPreferencePayload = (decision) => {
  const scheduleEntries = (
    Array.isArray(decision?.schedules) && decision.schedules.length
      ? decision.schedules
      : [decision?.schedule]
  ).filter(Boolean);

  const schedules = scheduleEntries
    .map((entry) => {
      const frequency = normalizeFrequency(entry?.frequency);
      const timingInUtc = getScheduleUtcTime(entry);
      const timingInIst = resolveIstClockTime(entry);
      if (!frequency.length || !timingInUtc) return null;
      return {
        frequency,
        timing_in_ist: timingInIst,
        timing_in_utc: timingInUtc,
      };
    })
    .filter(Boolean);

  const batchStartDate = toIsoBatchStartDate(decision?.startDate);

  if (!schedules.length || !batchStartDate) {
    throw new Error(
      "Batch confirmation requires start date, schedule days, and timing.",
    );
  }

  return {
    preferences: [
      {
        batch_start_date: batchStartDate,
        schedules,
      },
    ],
  };
};

export const buildProcessPreferencesRequestPayload = ({
  decision,
  userId,
  order,
  gatewayTenantId,
  gatewayBranchId,
}) => ({
  ...buildProcessPreferencePayload(decision),
  student: buildStudentPayload({
    userId,
    order,
    gatewayTenantId,
    gatewayBranchId,
  }),
});

export const hasExistingPaymentActivity = (order) => {
  if (!order) return false;
  if (Number(order.amount_paid) > 0) return true;
  return (order.payment_details || []).length > 0;
};
